
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Area, AreaChart } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const ForecastChart = () => {
  // Mock data - in a real app, this would come from an API
  const userForecastData = [
    // Historical data (actual)
    { month: "Jan", actual: 2100, benchmark: 2000, forecast: null },
    { month: "Feb", actual: 2300, benchmark: 2200, forecast: null },
    { month: "Mar", actual: 2500, benchmark: 2400, forecast: null },
    { month: "Apr", actual: 2700, benchmark: 2600, forecast: null },
    { month: "May", actual: 2900, benchmark: 2800, forecast: null },
    { month: "Jun", actual: 3200, benchmark: 3000, forecast: null },
    { month: "Jul", actual: 3400, benchmark: 3200, forecast: null },
    { month: "Aug", actual: 3600, benchmark: 3400, forecast: null },
    // Future forecasts
    { month: "Sep", actual: null, benchmark: 3600, forecast: 3850 },
    { month: "Oct", actual: null, benchmark: 3800, forecast: 4100 },
    { month: "Nov", actual: null, benchmark: 4000, forecast: 4350 },
    { month: "Dec", actual: null, benchmark: 4200, forecast: 4600 },
  ];

  const revenueForecastData = [
    // Historical data (actual)
    { month: "Jan", actual: 98000, benchmark: 95000, forecast: null },
    { month: "Feb", actual: 105000, benchmark: 100000, forecast: null },
    { month: "Mar", actual: 112000, benchmark: 105000, forecast: null },
    { month: "Apr", actual: 120000, benchmark: 110000, forecast: null },
    { month: "May", actual: 132000, benchmark: 115000, forecast: null },
    { month: "Jun", actual: 145000, benchmark: 120000, forecast: null },
    { month: "Jul", actual: 158000, benchmark: 125000, forecast: null },
    { month: "Aug", actual: 172000, benchmark: 130000, forecast: null },
    // Future forecasts
    { month: "Sep", actual: null, benchmark: 135000, forecast: 185000 },
    { month: "Oct", actual: null, benchmark: 140000, forecast: 199000 },
    { month: "Nov", actual: null, benchmark: 145000, forecast: 214000 },
    { month: "Dec", actual: null, benchmark: 150000, forecast: 230000 },
  ];

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Growth Forecast</CardTitle>
        <CardDescription>Compare actual performance against benchmarks and forecast</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList>
            <TabsTrigger value="users">User Growth</TabsTrigger>
            <TabsTrigger value="revenue">Revenue Growth</TabsTrigger>
          </TabsList>
          
          <TabsContent value="users">
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={userForecastData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="month" 
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    tickFormatter={(value) => `${(value / 1000).toFixed(1)}k`}
                    axisLine={false}
                    tickLine={false}
                  />
                  <ChartTooltip
                    content={
                      <div className="rounded-lg border bg-background p-2 shadow-md">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="font-medium">Month:</div>
                          <div className="font-medium text-right"></div>
                          <div className="flex items-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-[#8B5CF6]"></div>
                            <span>Actual:</span>
                          </div>
                          <div className="text-right"></div>
                          <div className="flex items-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-[#6B7280]"></div>
                            <span>Benchmark:</span>
                          </div>
                          <div className="text-right"></div>
                          <div className="flex items-center gap-1">
                            <div className="h-2 w-2 rounded-full bg-[#10B981]"></div>
                            <span>Forecast:</span>
                          </div>
                          <div className="text-right"></div>
                        </div>
                      </div>
                    }
                  />
                  <Area 
                    type="monotone" 
                    dataKey="actual" 
                    stroke="#8B5CF6" 
                    fillOpacity={1} 
                    fill="url(#colorActual)" 
                    name="Actual Users"
                    strokeWidth={2}
                    connectNulls
                  />
                  <Line 
                    type="monotone" 
                    dataKey="benchmark" 
                    stroke="#6B7280" 
                    strokeDasharray="5 5"
                    name="Benchmark"
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="forecast" 
                    stroke="#10B981" 
                    fillOpacity={1} 
                    fill="url(#colorForecast)" 
                    name="Forecast Users"
                    strokeWidth={2}
                    strokeDasharray="3 3"
                    connectNulls
                  />
                  <Legend />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>
          
          <TabsContent value="revenue">
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueForecastData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorActualRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorForecastRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    axisLine={false}
                    tickLine={false}
                  />
                  <ChartTooltip
                    formatter={(value, name) => {
                      if (!value) return ['-', name];
                      return name === 'actual' 
                        ? [`$${value.toLocaleString()}`, 'Actual'] 
                        : name === 'benchmark' 
                          ? [`$${value.toLocaleString()}`, 'Benchmark'] 
                          : [`$${value.toLocaleString()}`, 'Forecast'];
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="actual" 
                    stroke="#F59E0B" 
                    fillOpacity={1} 
                    fill="url(#colorActualRev)" 
                    name="Actual Revenue"
                    strokeWidth={2}
                    connectNulls
                  />
                  <Line 
                    type="monotone" 
                    dataKey="benchmark" 
                    stroke="#6B7280" 
                    strokeDasharray="5 5"
                    name="Benchmark"
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="forecast" 
                    stroke="#8B5CF6" 
                    fillOpacity={1} 
                    fill="url(#colorForecastRev)" 
                    name="Forecast Revenue"
                    strokeWidth={2}
                    strokeDasharray="3 3"
                    connectNulls
                  />
                  <Legend />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
